/**
 * NavBar component - Coherence-based navigation header for standalone Purview UI
 * Adapted from Shell's NavBar using Coherence v1 API.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Icon,
  Panel,
  PanelType,
  MessageBar,
  MessageBarType,
  Persona,
  PersonaSize,
  ResizeGroup,
  TooltipHost,
} from '@fluentui/react';
import {
  Header,
  HeaderButton,
  useResizeGroup,
  usePanels,
} from '@coherence-design-system/header';
import { ProfileCenter } from '@coherence-design-system/profile-center';
import { Template } from '@coherence-design-system/slot';
import { getUserInfo, logout, getAccessTokenSilent } from '../../auth/authUtils';

import type { IPanel } from '@fluentui/react';

const NavBar: React.FC = () => {
  const userInfo = getUserInfo();

  const [isProfileOpen, setProfileOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);

  const feedbackRef = useRef<IPanel>(null) as React.RefObject<IPanel>;
  const helpRef = useRef<IPanel>(null) as React.RefObject<IPanel>;
  const { currentPanel, openPanel, closePanel } = usePanels();
  const { onReduceData, onGrowData, onRenderData } = useResizeGroup({
    closePanel,
    currentPanel,
  });

  const resizeItems = React.useMemo(
    () => [
      {
        key: 'feedback',
        ariaLabel: 'Feedback',
        icon: 'Feedback',
        checked: currentPanel === feedbackRef,
        onClick: () => openPanel(feedbackRef),
        panelRef: feedbackRef,
        text: 'Feedback',
      },
      {
        key: 'help',
        ariaLabel: 'Help',
        icon: 'Help',
        checked: currentPanel === helpRef,
        onClick: () => openPanel(helpRef),
        panelRef: helpRef,
        text: 'Help',
      },
    ],
    [currentPanel, openPanel]
  );

  // Load user photo from Microsoft Graph (silent-only, no redirect for a cosmetic feature)
  useEffect(() => {
    let revokedUrl: string | undefined;
    let cancelled = false;
    const loadPhoto = async () => {
      try {
        const token = await getAccessTokenSilent(['https://graph.microsoft.com/User.Read']);
        if (!token || cancelled) return;
        const resp = await fetch(
          'https://graph.microsoft.com/v1.0/me/photos/64x64/$value',
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (resp.ok && !cancelled) {
          const blob = await resp.blob();
          const url = URL.createObjectURL(blob);
          revokedUrl = url;
          setPhotoUrl(url);
        }
      } catch {
        // Ignore photo load errors
      }
    };
    loadPhoto();
    return () => {
      cancelled = true;
      if (revokedUrl) URL.revokeObjectURL(revokedUrl);
    };
  }, []);

  const handleLogout = () => {
    logout().catch((error) => {
      console.error('Logout failed:', error);
    });
  };

  return (
    <div style={{ height: '48px' }} role="banner">
      <Header
        title="DLP Multi-Tenant Policy Management"
        showHamburger={false}
      >
        <Template slot="static-triggers">
          <TooltipHost content="Profile" id="profileTooltip">
            <HeaderButton
              aria-labelledby="profileTooltip"
              onClick={() => setProfileOpen(true)}
              checked={isProfileOpen}
            >
              <Persona
                hidePersonaDetails
                imageUrl={photoUrl}
                text={userInfo?.name ?? 'User'}
                size={PersonaSize.size28}
              />
            </HeaderButton>
          </TooltipHost>
        </Template>

        <Template slot="collapsible-triggers">
          <ResizeGroup
            styles={{ root: { background: 'transparent', height: '100%' } }}
            data={{ primary: resizeItems as any, overflow: [] } as any}
            onReduceData={onReduceData}
            onGrowData={onGrowData}
            onRenderData={onRenderData}
          />
        </Template>
      </Header>

      <ProfileCenter
        isOpen={isProfileOpen}
        onDismiss={() => setProfileOpen(false)}
        persona={{
          imageUrl: photoUrl,
          text: userInfo?.name ?? 'Signed in user',
          tertiaryText: userInfo?.email ?? '',
        }}
        actions={[
          {
            children: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Icon iconName="SignOut" />
                <span>Sign out</span>
              </span>
            ),
            onClick: (e) => {
              e.preventDefault();
              setProfileOpen(false);
              handleLogout();
            },
          },
        ]}
      />

      <Panel
        componentRef={feedbackRef}
        isOpen={Boolean(currentPanel === feedbackRef)}
        onDismiss={() => closePanel()}
        isLightDismiss
        headerText="Feedback"
        type={PanelType.smallFixedFar}
        closeButtonAriaLabel="Close feedback panel"
      >
        <MessageBar messageBarType={MessageBarType.info}>
          We can't open the feedback panel right now. Please try again later.
        </MessageBar>
      </Panel>

      <Panel
        componentRef={helpRef}
        isOpen={Boolean(currentPanel === helpRef)}
        onDismiss={() => closePanel()}
        isLightDismiss
        headerText="Help"
        type={PanelType.smallFixedFar}
        closeButtonAriaLabel="Close help panel"
      >
        <MessageBar messageBarType={MessageBarType.info}>
          We can't open Help right now. Please try again later.
        </MessageBar>
      </Panel>
    </div>
  );
};

export default NavBar;
